import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { posLabel } from './meta';

interface NewWordPayload {
  word: string;
  transcription: string;
  translation: string;
  partOfSpeech: string;
  definition: string;
}

interface AddWordPanelProps {
  partOfSpeechOptions: string[];
  onCancel: () => void;
  onAdd: (payload: NewWordPayload) => void;
  saving?: boolean;
}

const initial: NewWordPayload = {
  word: '',
  transcription: '',
  translation: '',
  partOfSpeech: 'NOUN',
  definition: '',
};

const AddWordPanel: React.FC<AddWordPanelProps> = ({
  partOfSpeechOptions,
  onCancel,
  onAdd,
  saving,
}) => {
  const [f, setF] = useState<NewWordPayload>(initial);
  const set = (k: keyof NewWordPayload) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setF((p) => ({ ...p, [k]: e.target.value }));

  const canAdd = f.word.trim().length > 0 && f.translation.trim().length > 0;

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd({
      word: f.word.trim(),
      transcription: f.transcription.trim(),
      translation: f.translation.trim(),
      partOfSpeech: f.partOfSpeech,
      definition: f.definition.trim(),
    });
  };

  const posOptions = partOfSpeechOptions.length
    ? partOfSpeechOptions
    : ['NOUN', 'VERB', 'ADJECTIVE', 'ADVERB'];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 2,
        boxShadow: '0 14px 34px -24px rgba(59,44,147,0.4)',
        mb: 2,
        p: { xs: 1.75, md: '18px 20px' },
      }}
    >
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1.125}
        sx={{ mb: 1.75 }}
      >
        <AddCircleOutlinedIcon
          sx={{ color: 'primary.main', fontSize: 22 }}
        />
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
          {'Нове слово'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton
          onClick={onCancel}
          size={'small'}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ flexWrap: 'wrap', mb: 1.5 }}
      >
        <TextField
          value={f.word}
          onChange={set('word')}
          label={'Слово (англ.)'}
          placeholder={'e.g. balance'}
          size={'small'}
          sx={{ flex: '1 1 180px' }}
        />
        <TextField
          value={f.transcription}
          onChange={set('transcription')}
          label={'Транскрипція'}
          placeholder={'[ˈbæləns]'}
          size={'small'}
          sx={{ flex: '1 1 150px' }}
        />
        <TextField
          value={f.translation}
          onChange={set('translation')}
          label={'Переклад'}
          placeholder={'рівновага'}
          size={'small'}
          sx={{ flex: '1 1 180px' }}
        />
        <TextField
          select
          value={f.partOfSpeech}
          onChange={set('partOfSpeech')}
          label={'Частина мови'}
          size={'small'}
          sx={{ flex: '0 1 170px', minWidth: 160 }}
        >
          {posOptions.map((p) => (
            <MenuItem key={p} value={p}>
              {posLabel(p)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        sx={{ flexWrap: 'wrap', rowGap: 1 }}
      >
        <TextField
          value={f.definition}
          onChange={set('definition')}
          label={'Визначення (необов\'язково)'}
          placeholder={'short definition…'}
          size={'small'}
          multiline
          maxRows={3}
          sx={{ flex: '1 1 320px' }}
        />
        <Stack direction={'row'} spacing={1.25}>
          <Button
            variant={'outlined'}
            onClick={onCancel}
            disabled={saving}
            sx={{ height: 42, px: 2 }}
          >
            {'Скасувати'}
          </Button>
          <Button
            variant={'contained'}
            startIcon={<CheckIcon />}
            onClick={handleAdd}
            disabled={!canAdd || saving}
            sx={{ height: 42, px: 2.25 }}
          >
            {'Додати слово'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AddWordPanel;
